import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeatureFlagContext = createContext(null);

export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [environment, setEnvironment] = useState('development');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`);
      setFlags(res.data.flags || {});
      setEnvironment(res.data.environment || 'development');
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      // Default to all enabled in case of error
      setFlags({});
    } finally {
      setLoading(false);
    }
  };

  const isEnabled = (flagKey) => {
    // Default to true if flag not found (fail open for better UX)
    return flags[flagKey] !== false;
  };

  const refreshFlags = () => {
    fetchFlags();
  };

  return (
    <FeatureFlagContext.Provider value={{ 
      flags, 
      environment, 
      loading, 
      isEnabled, 
      refreshFlags 
    }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// HOC for feature-flagged components
export const withFeatureFlag = (WrappedComponent, flagKey, FallbackComponent = null) => {
  return function FeatureFlaggedComponent(props) {
    const { isEnabled, loading } = useFeatureFlags();
    
    if (loading) {
      return null; // or a loading spinner
    }
    
    if (!isEnabled(flagKey)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Component wrapper for conditional rendering
export const FeatureFlag = ({ flag, children, fallback = null }) => {
  const { isEnabled, loading } = useFeatureFlags();
  
  if (loading) {
    return null;
  }
  
  return isEnabled(flag) ? children : fallback;
};

export default FeatureFlagContext;
